package fr.frux.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.saveable.listSaver
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.withFrameNanos
import fr.frux.core.maps.Location
import fr.frux.feature.map.aim.AimCircle
import fr.frux.feature.map.aim.MapAimState
import fr.frux.feature.treedetail.move.MoveTreeArgs
import fr.frux.feature.treedetail.move.TreeMoveAim
import fr.frux.shared.domain.model.DuplicateCandidate
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch

/** Ce que l'écran de déplacement reçoit de sa route. */
fun Route.MoveTree.toArgs(): MoveTreeArgs =
    MoveTreeArgs(
        treeId = treeId,
        latitude = latitude,
        longitude = longitude,
        speciesName = speciesName,
        scientificName = scientificName,
        canReportGone = canReportGone,
    )

/**
 * Point visé gardé d'un montage de l'entrée à l'autre. Nav3 peut démonter puis remonter l'écran
 * (changement de mode d'affichage, recréation de l'activité) : sans cette mémoire, l'épingle
 * repartirait de la position de l'arbre et le geste en cours serait perdu (même raison que
 * `rememberedAim` de l'assistant).
 */
class MoveAimMemory {
    var target: Location? = null
}

/** [MoveAimMemory] sauvegardée avec l'entrée de navigation. */
@Composable
fun rememberMoveAimMemory(): MoveAimMemory =
    rememberSaveable(
        saver =
            listSaver(
                save = { memory -> memory.target?.let { listOf(it.latitude, it.longitude) }.orEmpty() },
                restore = { saved ->
                    MoveAimMemory().apply { if (saved.size == 2) target = Location(saved[0], saved[1]) }
                },
            ),
    ) { MoveAimMemory() }

/**
 * Adapte le mode visée de la carte persistante au contrat du déplacement, sans que `feature-map`
 * et `feature-tree-detail` se connaissent. Avec [scope], [TreeMoveAim.start] attend une frame (et
 * [stillWanted], la route courante), pour la même raison que [asWizardAim] : le décalage de
 * l'épingle est écrit par un effet de la carte, qui peut passer après celui de l'écran. Avec
 * [memory], la visée reprend sur le dernier point visé, le cercle restant centré sur l'arbre.
 */
fun MapAimState.asMoveAim(
    scope: CoroutineScope? = null,
    memory: MoveAimMemory? = null,
    stillWanted: () -> Boolean = { true },
): TreeMoveAim = MoveAimAdapter(this, scope, memory, stillWanted)

private class MoveAimAdapter(
    private val aim: MapAimState,
    private val scope: CoroutineScope?,
    private val memory: MoveAimMemory?,
    private val stillWanted: () -> Boolean,
) : TreeMoveAim {
    private var pendingStart: Job? = null

    /** Arbre de la visée que cet adaptateur a démarrée, `null` avant le démarrage. */
    private var startedTreeId: Long? = null

    override val candidate: DuplicateCandidate? get() = aim.candidate

    override val distanceMeters: Double? get() = aim.targetDistanceMeters

    override fun currentTarget(): Location? = aim.currentTarget()

    override fun start(
        center: Location,
        radiusMeters: Double,
        scientificName: String,
        excludedTreeId: Long,
    ) {
        pendingStart?.cancel()
        val begin = {
            startedTreeId = excludedTreeId
            aim.start(memory?.target ?: center, AimCircle(center, radiusMeters, scientificName), excludedTreeId)
        }
        if (scope == null) {
            begin()
            return
        }
        pendingStart =
            scope.launch {
                withFrameNanos { }
                // La route a pu changer pendant la frame : jamais de visée hors du déplacement.
                if (stillWanted()) begin()
            }
    }

    override fun recenter() = aim.recenterOnCircle()

    override fun stop() {
        pendingStart?.cancel()
        pendingStart = null
        // L'écran sortant est disposé après la fin de sa transition : si l'assistant a déjà pris
        // la visée (« Ajouter un arbre ici »), l'arrêter ici la lui retirerait.
        val ours = aim.isActive && startedTreeId != null && aim.excludedTreeId == startedTreeId
        if (!ours) return
        memory?.target = aim.currentTarget()
        aim.stop()
    }
}
